"use client";

import { EditableValue } from "../forms/EditableValue";
import { formatMoneyShort } from "../lib/format";
import { type Provenance, ProvenancePill } from "./ProvenancePill";

/* -------------------------------------------------------------------------- */
/* RoleRow                                                                     */
/* -------------------------------------------------------------------------- */

export interface RoleRowProps {
  /** Workforce role name (e.g. "Registered Nurse"). */
  name: string;
  /** Full-time equivalents for this role. */
  ftes: number;
  /** Provenance of the FTE value. */
  ftesProvenance: Provenance;
  /** Average annual cost per FTE in dollars. */
  avgCost: number;
  /** Provenance of the avg-cost value. */
  avgCostProvenance: Provenance;
  /** Pre-computed total annual cost (ftes × avgCost). Read-only. */
  total: number;
  /** Called with the trimmed new string when the user edits FTEs. */
  onSaveFtes: (next: string) => void;
  /** Called with the trimmed new string when the user edits avg cost. */
  onSaveAvgCost: (next: string) => void;

  // ── Optional: expandable breakdown ───────────────────────────
  /** When true, render a chevron toggle in the name cell. */
  expandable?: boolean;
  /** Controlled expanded state (parent owns it). */
  expanded?: boolean;
  /** Called when the chevron is clicked. */
  onToggleExpand?: () => void;
  /** Small running indicator, e.g. "70 of 100 FTEs". Rendered under FTEs. */
  subSumIndicator?: string;
  /** When true, the sub-sum indicator renders in the error color. */
  subSumError?: boolean;

  // ── Optional: custom role affordances ────────────────────────
  /** When provided, the name becomes inline-editable (custom roles). */
  onSaveName?: (next: string) => void;
  /** When provided, render a remove button (custom roles). */
  onRemove?: () => void;
}

const numCls = "text-right tabular-nums";

/**
 * A `<tr>` row for the Workforce table.
 *
 * Column order:
 * | Role | FTEs | FTEs prov | Avg cost | Avg cost prov | Total |
 */
export function RoleRow({
  name,
  ftes,
  ftesProvenance,
  avgCost,
  avgCostProvenance,
  total,
  onSaveFtes,
  onSaveAvgCost,
  expandable = false,
  expanded = false,
  onToggleExpand,
  subSumIndicator,
  subSumError = false,
  onSaveName,
  onRemove,
}: RoleRowProps) {
  return (
    <tr className="border-b border-(--border-default) last:border-b-0 text-sm">
      <td className="py-2 pr-3 text-(--fg-primary)">
        <div className="flex items-center gap-1.5">
          {expandable ? (
            <button
              type="button"
              onClick={onToggleExpand}
              aria-label={expanded ? `Collapse ${name}` : `Expand ${name}`}
              aria-expanded={expanded}
              className="shrink-0 text-(--fg-secondary) hover:text-(--fg-primary)"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                aria-hidden="true"
                className={`transition-transform ${expanded ? "rotate-90" : ""}`}
              >
                <path
                  d="M4 2l4 4-4 4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
          ) : (
            <span className="inline-block w-3 shrink-0" aria-hidden="true" />
          )}
          {onSaveName ? (
            <EditableValue
              value={name}
              onSave={onSaveName}
              ariaLabel="Role name"
            />
          ) : (
            <span>{name}</span>
          )}
          {onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove ${name}`}
              className="ml-1 text-(--fg-secondary) hover:text-(--fg-primary) text-xs"
            >
              ✕
            </button>
          ) : null}
        </div>
      </td>
      <td className={`py-2 pr-2 ${numCls}`}>
        <EditableValue
          value={ftes.toLocaleString()}
          align="right"
          type="number"
          formatThousands
          onSave={onSaveFtes}
        />
        {subSumIndicator ? (
          <div
            className={`text-[10px] mt-0.5 ${
              subSumError
                ? "text-(--status-error-text)"
                : "text-(--fg-secondary)"
            }`}
          >
            {subSumIndicator}
          </div>
        ) : null}
      </td>
      <td className="py-2 pr-3">
        <ProvenancePill kind={ftesProvenance} />
      </td>
      <td className={`py-2 pr-2 ${numCls}`}>
        <EditableValue
          value={avgCost.toLocaleString()}
          align="right"
          type="number"
          prefix="$"
          formatThousands
          onSave={onSaveAvgCost}
        />
      </td>
      <td className="py-2 pr-3">
        <ProvenancePill kind={avgCostProvenance} />
      </td>
      <td className={`py-2 ${numCls} text-(--fg-secondary) font-medium`}>
        {formatMoneyShort(total)}
      </td>
    </tr>
  );
}
